import React, { useState } from "react";
import { TSessionSummaryReport, Report } from "../../types/types";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import { useNavigate } from "react-router-dom";
import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./generateTreatmentReportsSummary.module.css";
import UserInfo from "../../components/userInfo/userInfo";
import { FormikHelpers, useFormik } from "formik";
import LanguageToSummary from "../../components/modals/languageToSummary/languageToSummary";
import Modal from "../../components/modals/modal/Modal";
import i18n from "../../i18n";
import { IUserReport } from "../../services/types/users.types";
import {
  useGenerateMeetingReportMutation,
  useGetSessionSummaryReportQuery,
} from "../../services/api/sessionSummary.api";
import { treatmentReportValidationSchema } from "../../utils/validation";
import VenchiMessage from "../venchiMessage/venchiMessage";
import FeatureUsageCounter from "../FeatureUsageCounter/FeatureUsageCounter";
import { useActions } from "../../services/hooks/useActions";
import ReportSuccessPopup from "../modals/reportSuccessPopup/reportSuccessPopup";
import SignaturePadModal from "../modals/signaturePadModal/signaturePadModal";

const GenerateTreatmentReportSummary = ({
  user,
  onClose,
}: {
  user: IUserReport;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { clearSessionSummary } = useActions();
  const [generatedReport, setGeneratedReport] = useState<Report | null>(null);
  const [generateMeetingReport, { isLoading: isLoad, error: err }] =
    useGenerateMeetingReportMutation();
  const [languageToSummary, setLanguageToSummary] = useState<string | null>(
    null
  );
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  const {
    data: reportData,
    isLoading,
    error,
  } = useGetSessionSummaryReportQuery(
    {
      userGuid: user.Users?.Guid || "",
      language: languageToSummary!,
        },
    {
      skip: !languageToSummary,
    }
  );

  function createPdf(values: TSessionSummaryReport) {
    values.language = languageToSummary ? languageToSummary : i18n.language;
    generateMeetingReport({
      body: values,
      userGuid: user.Users.Guid || "1",
    })
      .unwrap()
      .then((res) => {
        clearSessionSummary();
        setGeneratedReport(res);
        //console.log("Report generated successfully:", res);
      })
      .catch((err) => {
        console.error(t("ERROR_CREATING_PDF"), err);
      });
  }

  const handleSubmit = async (
    values: TSessionSummaryReport,
    formikHelpers: FormikHelpers<TSessionSummaryReport>
  ) => {
    formikHelpers.setTouched(
      Object.keys(values).reduce((acc, key) => {
        acc[key as keyof TSessionSummaryReport] = true;
        return acc;
      }, {} as Record<keyof TSessionSummaryReport, boolean>)
    );

    // מפעילים וולידציה
    const errors = await formikHelpers.validateForm();

    if (Object.keys(errors).length > 0) {
      formikHelpers.setSubmitting(false);
      return;
    }

    createPdf(values);
    formikHelpers.setSubmitting(false);
  };

  const formik = useFormik<TSessionSummaryReport>({
    initialValues: reportData!,
    onSubmit: handleSubmit,
    validationSchema: treatmentReportValidationSchema,
    enableReinitialize: true,
  });

  const parseBooleanSelectValue = (val: string): boolean | null => {
    if (val === "true") return true;
    if (val === "false") return false;
    return null;
  };

  const handleLanguageSelect = (lang: string) => {
    setLanguageToSummary(lang);
  };

  if (!languageToSummary) {
    return (
      <Modal
        onClose={() => {
          navigate(-1);
        }}
      >
        <LanguageToSummary onSelectLanguage={handleLanguageSelect} />
      </Modal>
    );
  }

  if (isLoading)
    return (
      <VenchiMessage
        message={[
          `${t("VENCHI_GENERATING_REPORT")}`,
          `${t("PATIENCE_PAYS")}`,
          `${t("PLEASE_WAIT_A_MOMENT")}`,
          `${t("EFFORT_FOR_QUALITY")}`,
        ]}
      />
    );
  if (error)
    return (
      <article className={styles.section}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"center"}
        />
        <VenchiMessage message={`${t("ERROR_TRY_AGAIN")}`} />;{" "}
      </article>
    );
  if (isLoad) return <VenchiMessage message={t("REPORT_LOADING")} />;
  if (err)
    return (
      <article className={styles.section}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"center"}
        />
        <VenchiMessage message={t("REPORT_ERROR")} />
      </article>
    );

  if (generatedReport?.fileUrl) {
    return (
      <Modal onClose={onClose}>
        <ReportSuccessPopup
          onClose={onClose}
          reportUrl={generatedReport.fileUrl}
        />
      </Modal>
    );
  }

  if (!formik.values)
    return <VenchiMessage message={`${t("NO_REPORT_AVAILABLE")}`} />;

  return (
    <>
      <VenchiMessage
        message={`${t("SYSTEM_GENERATED_MESSAGE")} `}
        position="topLeft"
      />
      <article className={styles.section}>
        <BackArrowButton
          text={t("PREVIOUS_SCREEN")}
          onClick={onClose}
          position={"center"}
        />
        <h3 className={styles.title}>
          {t("SESSION_SUMMARY_REPORT_FOR_USER")} 
          {user?.Users.FirstName ?? ""} 
          {user?.Users.LastName ?? ""} ({t("BETA_VERSION")})
        </h3>
        {user && (
          <div>
            <div className={styles.userInfo}>
              <UserInfo user={user} />
            </div>

            <form className={styles.form} onSubmit={formik.handleSubmit}>
              <div className={styles.containerInfo}>
                <div className={styles.headerRow}>
                  <div className={styles.labelWithValue}>
                    <label>{t("DATE")}:</label>
                    <div className={styles.readonlyField}>
                      {formik.values.reportDate
                        ? new Date(formik.values.reportDate).toLocaleDateString(
                            "he-IL"
                          )
                        : ""}
                    </div>
                  </div>
                  <div className={styles.labelWithValue}>
                    <label>{t("INQUIRY_NAME_HEADER")}:</label>
                    <div className={styles.readonlyField}>
                      {formik.values.therapistName ?? ""}
                    </div>
                  </div>
                </div>
                <div className={styles.readonlyField}>
                  {formik.values.reportsIncludedInSummary ?? ""}
                </div>
                <div className={styles.flexRowWrap}>
                  <div className={styles.labelWithValue}>
                    <label>{t("FIRST_NAME")}:</label>
                    <div className={styles.readonlyField}>
                      {formik.values.patientFirstName ?? ""}
                    </div>
                  </div>
                  <div className={styles.labelWithValue}>
                    <label>{t("LAST_NAME")}:</label>
                    <div className={styles.readonlyField}>
                      {formik.values.patientLastName ?? ""}
                    </div>
                  </div>

                  <div className={styles.labelWithValue}>
                    <label>{t("PATIENT_ID")} </label>
                    <div className={styles.readonlyField}>
                      {user?.Users?.Passport
                        ? user.Users.Passport
                        : t("NO_DATA")}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("REFERRAL_REASON")}
                  <textarea
                    className={styles.textarea}
                    name="referralReasons"
                    value={formik.values.referralReasons}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.referralReasons &&
                  formik.errors.referralReasons && (
                    <div className={styles.error}>
                      {t(formik.errors.referralReasons)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("HISTORY")}
                  <textarea
                    className={styles.textarea}
                    name="lifeHistory"
                    value={formik.values.lifeHistory}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.lifeHistory && formik.errors.lifeHistory && (
                  <div className={styles.error}>
                    {t(formik.errors.lifeHistory)}
                  </div>
                )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("MENTAL_STATUS_AT_INTAKE")}
                  <textarea
                    className={styles.textarea}
                    name="mentalStatusAtIntake"
                    value={formik.values.mentalStatusAtIntake}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.mentalStatusAtIntake &&
                  formik.errors.mentalStatusAtIntake && (
                    <div className={styles.error}>
                      {t(formik.errors.mentalStatusAtIntake)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("FUNCTIONING_AT_INTAKE")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="functioningAtIntake"
                    value={formik.values.functioningAtIntake}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.functioningAtIntake &&
                  formik.errors.functioningAtIntake && (
                    <div className={styles.error}>
                      {t(formik.errors.functioningAtIntake)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("DANGEROUS_OBSERVATION")}
                  <select
                    className={styles.gtrsSelect}
                    name="isDangerObserved"
                    value={
                      formik.values.isDangerObserved === null
                        ? ""
                        : String(formik.values.isDangerObserved)
                    }
                    onChange={(e) => {
                      const parsedVal = parseBooleanSelectValue(e.target.value);
                      formik.setFieldValue("isDangerObserved", parsedVal);
                    }}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">{t("NOT_SELECTED")} </option>
                    <option value="true">{t("YES")}</option>
                    <option value="false">{t("NO")}</option>
                  </select>
                </label>
                {formik.touched.isDangerObserved &&
                  formik.errors.isDangerObserved && (
                    <div className={styles.error}>
                      {t(formik.errors.isDangerObserved)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("ADDICTION_OBSERVATION")}
                  <select
                    className={styles.gtrsSelect}
                    name="isAddictionObserved"
                    value={
                      formik.values.isAddictionObserved === null
                        ? ""
                        : formik.values.isAddictionObserved.toString()
                    }
                    onChange={(e) => {
                      const parsedVal = parseBooleanSelectValue(e.target.value);
                      formik.setFieldValue("isAddictionObserved", parsedVal);
                    }}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">{t("NOT_SELECTED")} </option>
                    <option value="true">{t("YES")}</option>
                    <option value="false">{t("NO")}</option>
                  </select>
                </label>
                {formik.touched.isAddictionObserved &&
                  formik.errors.isAddictionObserved && (
                    <div className={styles.error}>
                      {t(formik.errors.isAddictionObserved)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("INITIAL_DIAGNOSIS")}
                  <textarea
                    className={styles.textarea}
                    name="preliminaryDiagnosisICD10_DSM5"
                    value={formik.values.preliminaryDiagnosisICD10_DSM5}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.preliminaryDiagnosisICD10_DSM5 &&
                  formik.errors.preliminaryDiagnosisICD10_DSM5 && (
                    <div className={styles.error}>
                      {t(formik.errors.preliminaryDiagnosisICD10_DSM5)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("INITIAL_TREATMENT_PLAN")}
                  <textarea
                    className={styles.textarea}
                    name="treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes"
                    value={
                      formik.values
                        .treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched
                  .treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes &&
                  formik.errors
                    .treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes && (
                    <div className={styles.error}>
                      {t(
                        formik.errors
                          .treatmentPlanAndGoals_AsDeterminedAtIntake_TreatmentFrequency_TherapeuticGoals_OtherNotes
                      )}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("TREATMENT_PROGRESS")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse"
                    value={
                      formik.values
                        .treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched
                  .treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse &&
                  formik.errors
                    .treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse && (
                    <div className={styles.error}>
                      {t(
                        formik.errors
                          .treatmentCourseDescription_NumberOfSessionsThemesEngagedResponse
                      )}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("CURRENT_MENTAL_STATUS")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace"
                    value={
                      formik.values
                        .currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched
                  .currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace &&
                  formik.errors
                    .currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace && (
                    <div className={styles.error}>
                      {t(
                        formik.errors
                          .currentMentalStatusAppearanceBehaviorCooperationMoodAffectPerceptionThinkingProcessAndPace
                      )}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("CURRENT_FUNCTIONING")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="currentFunctioning"
                    value={formik.values.currentFunctioning}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.currentFunctioning &&
                  formik.errors.currentFunctioning && (
                    <div className={styles.error}>
                      {t(formik.errors.currentFunctioning)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("THERAPEUTIC_GOALS_ACHIEVED")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="therapeuticGoalsAchieved"
                    value={formik.values.therapeuticGoalsAchieved}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.therapeuticGoalsAchieved &&
                  formik.errors.therapeuticGoalsAchieved && (
                    <div className={styles.error}>
                      {t(formik.errors.therapeuticGoalsAchieved)}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("UPDATED_DIAGNOSIS")}
                  <textarea
                    className={styles.textarea}
                    name="currentWorkingDiagnosisAccordingTo_ICD10_DSM5"
                    value={
                      formik.values
                        .currentWorkingDiagnosisAccordingTo_ICD10_DSM5
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
                {formik.touched.currentWorkingDiagnosisAccordingTo_ICD10_DSM5 &&
                  formik.errors
                    .currentWorkingDiagnosisAccordingTo_ICD10_DSM5 && (
                    <div className={styles.error}>
                      {t(
                        formik.errors
                          .currentWorkingDiagnosisAccordingTo_ICD10_DSM5
                      )}
                    </div>
                  )}
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("RECOMMENDATIONS_FOR_CONTINUATION")}
                  <textarea
                    className={styles.textarea}
                    name="recommendationsIncludingTreatmentPlanAndUpdatedGoalsIfContinuedTreatmentIsAdvised"
                    value={
                      formik.values
                        .recommendationsIncludingTreatmentPlanAndUpdatedGoalsIfContinuedTreatmentIsAdvised
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>
                  {t("ADDITIONAL_INFORMATION")}{" "}
                  <textarea
                    className={styles.textarea}
                    name="additionalRelevantInformationToAdd"
                    value={formik.values.additionalRelevantInformationToAdd}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </label>
              </div>

              <div
                className={`${styles.formField} ${styles.formField_type_wide}`}
              >
                <label className={styles.gtrsLabel}>{t("SIGNATURE")}</label>
                {formik.values.therapistSignature?.startsWith("data:image/") ? (
                  <div className={styles.signaturePreview}>
                    <img
                      src={formik.values.therapistSignature}
                      alt={t("SIGNATURE")}
                      className={styles.signaturePreview__img}
                    />
                    <button
                      type="button"
                      className={styles.signatureChangeBtn}
                      onClick={() => setShowSignatureModal(true)}
                    >
                      {t("SIGNATURE_CHANGE")}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.signatureSignBtn}
                    onClick={() => setShowSignatureModal(true)}
                  >
                    {t("DIGITALLY_SIGNED")}
                  </button>
                )}
                {formik.touched.therapistSignature &&
                  formik.errors.therapistSignature && (
                    <div className={styles.error}>
                      {t(formik.errors.therapistSignature)}
                    </div>
                  )}
                {formik.touched.isDigitallySigned &&
                  formik.errors.isDigitallySigned && (
                    <div className={styles.error}>
                      {t(formik.errors.isDigitallySigned)}
                    </div>
                  )}
              </div>
              {showSignatureModal && (
                <SignaturePadModal
                  onClose={() => setShowSignatureModal(false)}
                  onConfirm={(signatureDataUrl) => {
                    formik.setFieldValue("therapistSignature", signatureDataUrl);
                    formik.setFieldValue("isDigitallySigned", true);
                    setShowSignatureModal(false);
                  }}
                />
              )}

              <div className={styles.formField_type}>
                <FeatureUsageCounter featureKey="NARRATIVE_REPORTS" />
                <button
                  type="button"
                  className={`${styles.pdfSaveButton} ${
                    !formik.isValid || !formik.dirty
                      ? styles.disabledButton
                      : ""
                  }`}
                  onClick={() => {
                    if (formik.isValid && formik.dirty) {
                      formik.handleSubmit();
                    } else {
                      // סמן את כל השדות כ-touched כדי להראות את השגיאות
                      formik.setTouched(
                        Object.keys(formik.values).reduce((acc, key) => {
                          acc[key] = true;
                          return acc;
                        }, {} as Record<string, boolean>)
                      );
                    }
                  }}
                >
                  <svg className={styles.pdfIcon} viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm0 2l4 4h-4zM9 13H8v4H7v-4H6v-1h3zm4 2h-1v2h-1v-5h2a1.5 1.5 0 0 1 0 3zm0-2h-1v1h1a.5.5 0 0 0 0-1zm4 1h-1v3h-1v-3h-1v-1h3z" />
                  </svg>
                  {t("PDF_SAVE")}
                </button>
              </div>
            </form>
          </div>
        )}
      </article>
    </>
  );
};

export default GenerateTreatmentReportSummary;
